// §11.1 / §7.5 — the version-history scrubber.
//
// A scrubber, not a list: dragging the slider *shows* the file as it was, so Restore is a
// decision rather than a gamble. §7.5 makes the restore itself a write, so it appends a version
// and is undoable — which is why the count goes up by one every time you use it.
import { useCallback, useEffect, useState } from 'react';
import {
  DIFF_PAYLOAD_MIME,
  type DiffLaunchPayload,
  type ExplorerShellAccess,
  type FileEntry,
  type HangarAPI,
  type HangarFsApi,
  type VersionEntry,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { formatSize, formatWhen } from './model';
import { peekKind } from './peek-kinds';

export function VersionHistory({
  fs,
  access,
  api,
  entry,
  onClose,
}: {
  fs: HangarFsApi;
  access: ExplorerShellAccess;
  /** §34.5's Compare launches Diff through `launch.app`'s argument (D-540). */
  api: HangarAPI;
  entry: FileEntry;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<VersionEntry[] | null>(null);
  const [index, setIndex] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** §34.5 — version-vs-version: which version the user pinned as the other side, if any. */
  const [against, setAgainst] = useState<string | null>(null);

  const load = useCallback(() => {
    void fs
      .versions(entry.path)
      .then((listed) => {
        setVersions(listed);
        setIndex(0);
      })
      .catch((cause: unknown) => {
        setVersions([]);
        setError(cause instanceof Error ? cause.message : String(cause));
      });
  }, [fs, entry.path]);

  useEffect(load, [load]);

  const selected = versions?.[index] ?? null;

  useEffect(() => {
    if (!selected) {
      setPreview(null);
      return;
    }
    if (peekKind(entry.path) !== 'text') {
      setPreview(null);
      return;
    }
    let cancelled = false;
    void access
      .readVersion(entry.path, selected.id)
      .then((bytes) => {
        if (!cancelled) setPreview(new TextDecoder().decode(bytes));
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [access, entry.path, selected]);

  /**
   * §34.5 — **Compare**, the scrubber's second entry point into Diff.
   *
   * Explorer reads both sides itself and hands over the **text**, which is not a shortcut: a
   * version's bytes are reachable only through `ExplorerShellAccess.readVersion` (M14), so a payload
   * of paths and ids would name something the receiver cannot fetch (D-543). It is a §10 delivery,
   * so Diff gets content under a payload token and no filesystem grant.
   */
  const compare = useCallback(
    (other: VersionEntry | null) => {
      const selected = versions?.[index] ?? null;
      if (!selected) return;
      void (async () => {
        try {
          const decode = (bytes: Uint8Array): string => new TextDecoder().decode(bytes);
          const a: DiffLaunchPayload['a'] = {
            path: entry.path,
            versionId: selected.id,
            text: decode(await access.readVersion(entry.path, selected.id)),
          };
          // No `versionId` on the right side means *current*, which is what §34.5's optional field
          // is for and what the header then says.
          const b: DiffLaunchPayload['b'] =
            other === null
              ? { path: entry.path, text: await fs.readText(entry.path) }
              : {
                  path: entry.path,
                  versionId: other.id,
                  text: decode(await access.readVersion(entry.path, other.id)),
                };
          await api.launch.app('diff', {
            items: [{ mime: DIFF_PAYLOAD_MIME, text: JSON.stringify({ a, b } satisfies DiffLaunchPayload) }],
          });
        } catch (cause: unknown) {
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      })();
    },
    [access, api, entry.path, fs, index, versions],
  );

  const selectedIsPinned = selected !== null && against === selected.id;

  return (
    <aside className="explorer-history" aria-label={COPY.explorer.versionHistory} data-testid="explorer-history">
      <header className="explorer-history-head">
        <span data-testid="explorer-history-name">{entry.name}</span>
        <button
          type="button"
          className="explorer-button"
          aria-label={COPY.explorer.close}
          data-testid="explorer-history-close"
          onClick={onClose}
        >
          ✕
        </button>
      </header>

      {versions === null ? null : versions.length === 0 ? (
        <p className="explorer-empty" data-testid="explorer-history-empty">
          {error ?? COPY.explorer.noVersions}
        </p>
      ) : (
        <>
          <input
            type="range"
            className="explorer-history-slider"
            data-testid="explorer-history-slider"
            aria-label={COPY.explorer.versionHistory}
            min={0}
            max={versions.length - 1}
            step={1}
            value={index}
            onChange={(event) => setIndex(Number(event.target.value))}
          />
          <p className="explorer-history-when" data-testid="explorer-history-when">
            {COPY.explorer.versionOf(index + 1, versions.length)} ·{' '}
            {formatWhen(selected?.createdAt ?? 0)} · {formatSize(selected?.size ?? 0)}
          </p>
          {preview === null ? null : (
            <pre className="explorer-history-preview" data-testid="explorer-history-preview">
              {preview}
            </pre>
          )}
          {/* §34.5 — version vs current, and version vs version. Two buttons rather than a mode:
              the common case is one click, and pinning a second version is the rarer one. */}
          <div className="explorer-history-compare">
            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-history-compare"
              onClick={() => compare(null)}
            >
              {COPY.diff.compareWithCurrent}
            </button>
            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-history-pin"
              data-active={String(against !== null)}
              onClick={() => {
                // Pin this version as the other side; a second click on the *same* version unpins,
                // and clicking Compare while something is pinned diffs the two versions.
                if (!selected) return;
                setAgainst((current) => (current === selected.id ? null : selected.id));
              }}
            >
              {against === null
                ? COPY.diff.compare
                : selectedIsPinned
                  ? COPY.diff.comparePinned
                  : COPY.diff.compareAgainstPinned}
            </button>
            {against !== null && !selectedIsPinned ? (
              <button
                type="button"
                className="explorer-button"
                data-testid="explorer-history-compare-pinned"
                onClick={() => compare(versions?.find((candidate) => candidate.id === against) ?? null)}
              >
                {COPY.diff.compareTwo}
              </button>
            ) : null}
          </div>

          <button
            type="button"
            className="explorer-button"
            data-testid="explorer-history-restore"
            onClick={() => {
              if (!selected) return;
              void fs
                .restore(entry.path, selected.id)
                // A restore appends a version, so the list it came from is already stale.
                .then(load)
                .catch((cause: unknown) =>
                  setError(cause instanceof Error ? cause.message : String(cause)),
                );
            }}
          >
            {COPY.explorer.restoreVersion}
          </button>
          {error ? (
            <p className="explorer-empty" data-testid="explorer-history-error">
              {error}
            </p>
          ) : null}
        </>
      )}
    </aside>
  );
}
